import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  NotePencil, Lightning, Moon, Stack, ChatCircleDots, ArrowRight, Sparkle, ShieldCheck,
  Lock, Fire, Export, Brain, GitMerge, Target, Waves, Path, Star,
} from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Disclaimer } from "@/components/Disclaimer";

const fadeUp = {
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, ease: "easeOut" },
};

const FEATURES = {
  journal: {
    icon: NotePencil, color: "#34D399", eyebrow: "The Journal",
    h0: "A journal that", h1: "understands your patterns.",
    sub: "Not just a place to vent — a calm, private space that connects what you write to the patterns your assessments reveal, and reflects them back with insight.",
    points: [
      { icon: Sparkle, t: "AI reflection on every entry", d: "Write freely. Get one warm, insightful reflection and a gentle question — grounded in your actual patterns." },
      { icon: GitMerge, t: "Recurring-theme detection", d: "The journal notices the triggers, moods and patterns that keep surfacing, and surfaces them for you." },
      { icon: Lock, t: "Passcode lock", d: "Lock your journal behind a passcode. Your inner world stays yours." },
      { icon: Fire, t: "Day streaks & focus mode", d: "A distraction-free fullscreen composer and a streak to keep the habit alive." },
      { icon: Export, t: "Export anytime", d: "Download everything you've written as clean markdown. Your words, your data." },
    ],
    ctaPrimary: { label: "Start journaling free", to: "/dashboard" },
  },
  "inner-practices": {
    icon: Lightning, color: "#A78BFA", eyebrow: "Inner Practices",
    h0: "Tiny practices that", h1: "rewire the pattern.",
    sub: "Insight without action fades. Inner Practices turn your specific patterns into small, situational micro-practices you can actually do — in the moment, this week, over time.",
    points: [
      { icon: Target, t: "Matched to your patterns", d: "Practices are recommended from your living profile's growth edges — not generic advice." },
      { icon: Waves, t: "A 'Do This Now' reset", d: "Feeling flooded? Get an in-the-moment reset, a practice, and a prompt tuned to what you feel." },
      { icon: Path, t: "Staged for real change", d: "Interrupt → reframe → rehearse → restore. A clear arc from reflex to new response." },
      { icon: Fire, t: "Weekly plan & progress", d: "A simple weekly plan and a record of what's actually helping you — with usefulness ratings." },
    ],
    ctaPrimary: { label: "Explore practices", to: "/dashboard" },
  },
  symbolic: {
    icon: Moon, color: "#818CF8", eyebrow: "The Pattern Mirror",
    h0: "A symbolic mirror,", h1: "in plain language.",
    sub: "An optional reflective lens that reads symbolic signals from your birth details and translates them into plain-language insight about your strengths, hidden beliefs and growth edges — no charts, no jargon, no predictions.",
    points: [
      { icon: Sparkle, t: "Plain language only", d: "No planets, no astrology jargon. Just clear reflection about how you tend to think, feel and relate." },
      { icon: Brain, t: "Hidden beliefs surfaced", d: "Gently names the unspoken assumptions that may be quietly driving your behaviour." },
      { icon: GitMerge, t: "Woven into your profile", d: "Aligns the symbolic themes with your assessment patterns — where they match and where they pull." },
      { icon: ShieldCheck, t: "Private by design", d: "Your birth details are used only to compute your mirror, on our server. It's strictly opt-in." },
    ],
    ctaPrimary: { label: "Reveal your mirror", to: "/dashboard" },
  },
  assessments: {
    icon: Stack, color: "#14B8A6", eyebrow: "The Assessments",
    h0: "18 free assessments.", h1: "One living profile.",
    sub: "Each assessment is a doorway into a hidden pattern — attachment, shadow, self-worth, burnout, conflict style and more. Take one or take them all; every result feeds the same evolving map of who you really are.",
    points: [
      { icon: Brain, t: "Grounded in real psychology", d: "Attachment science, shadow work, the Big Five, nervous-system theory — not random quizzes." },
      { icon: Sparkle, t: "A full result, instantly", d: "Rich, personalized AI analysis on every result. No paywall, ever." },
      { icon: GitMerge, t: "Cross-pattern synthesis", d: "See where your assessments agree — the recurring patterns worth working on first." },
      { icon: Path, t: "Track how you change", d: "Re-take anytime and watch the deltas. Your inner world isn't fixed — neither is your map." },
    ],
    ctaPrimary: { label: "Browse all assessments", to: "/#all" },
  },
  "ai-coach": {
    icon: ChatCircleDots, color: "#5EEAD4", eyebrow: "The AI Coach",
    h0: "A coach that", h1: "remembers everything.",
    sub: "Most chatbots start from zero every time. Your AI Coach is grounded in your completed assessments, your recurring patterns and your recent journals — so the guidance is about you, not generic.",
    points: [
      { icon: Brain, t: "Knows your patterns", d: "Trained on your living profile — your archetypes, growth edges and recurring themes." },
      { icon: Sparkle, t: "Proactive nudges", d: "It notices what keeps surfacing for you and gently opens the conversation that matters." },
      { icon: ShieldCheck, t: "Warm, direct, safe", d: "Depth-psychology informed, never pathologizing — and clear about where therapy belongs." },
      { icon: GitMerge, t: "Connected to your work", d: "References your journals and practices so every conversation builds on the last." },
    ],
    ctaPrimary: { label: "Meet your coach", to: "/dashboard" },
  },
};

export default function Feature() {
  const { key } = useParams();
  const f = FEATURES[key];
  useEffect(() => { window.scrollTo(0, 0); }, [key]);

  if (!f) {
    return (
      <div className="min-h-screen bg-slate-950"><Navbar />
        <div className="mx-auto max-w-xl px-5 pt-40 text-center">
          <h1 className="font-heading text-3xl text-slate-100">Feature not found</h1>
          <Link to="/" className="mt-4 inline-block text-teal-400">← Back home</Link>
        </div>
      </div>
    );
  }

  const Icon = f.icon;

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden grain-overlay">
        <div className="absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full blur-[140px]" style={{ background: `${f.color}22` }} />
        <div className="relative mx-auto max-w-5xl px-5 pb-20 pt-36 sm:px-8 sm:pt-44">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-900/50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] backdrop-blur" style={{ color: f.color }}>
              <Icon size={14} weight="fill" /> {f.eyebrow}
            </span>
            <h1 className="mt-6 font-heading text-4xl font-light leading-[1.05] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              <span className="block">{f.h0}</span>
              <span className="block font-medium" style={{ color: f.color }}>{f.h1}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">{f.sub}</p>
            <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Link to={f.ctaPrimary.to} className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-slate-950 transition-all duration-300" style={{ background: f.color }} data-testid="feature-cta-btn">
                {f.ctaPrimary.label} <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
              <span className="text-sm text-slate-500">Free · Part of your living profile</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* POINTS */}
      <section className="border-t border-slate-900 bg-slate-950 py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="grid gap-6 md:grid-cols-2">
            {f.points.map((p, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: (i % 2) * 0.08 }} className="rounded-2xl border border-slate-800 bg-slate-900 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-slate-700">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${f.color}1a`, color: f.color }}><p.icon size={22} weight="duotone" /></span>
                <h3 className="mt-5 font-heading text-lg font-medium text-slate-100">{p.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-slate-900 bg-slate-950 py-24 grain-overlay">
        <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full blur-[120px]" style={{ background: `${f.color}1a` }} />
        <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Icon size={40} weight="duotone" style={{ color: f.color }} className="mx-auto" />
          <h2 className="mt-6 font-heading text-3xl font-light tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">It all builds<br /><span className="font-medium" style={{ color: f.color }}>one living profile.</span></h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-400">Start with a single free assessment — everything else grows from there.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/#all" className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-semibold text-slate-950 hover:bg-teal-400">Take a free assessment <ArrowRight size={16} weight="bold" /></Link>
            <Link to="/dashboard" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-7 py-3.5 text-sm font-medium text-slate-200 hover:border-slate-500">Open your dashboard</Link>
          </div>
          <div className="mx-auto mt-12 max-w-xl"><Disclaimer /></div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export { FEATURES };
